import {
  Alert,
  Badge,
  BadgeColor,
  Button,
  Column,
  DropdownButtonCell,
  DropdownButtonCellComponentProps,
  FieldContainer,
  Input,
  MenuItemProps,
  Select,
  SelectOptionElement,
  Table,
} from '@odin-labs/components';
import { PlusIcon, TrashIcon } from 'components/icons';
import React from 'react';
import {
  ReaderHealth,
  ReaderKindBadge,
  type ReaderDisplayFields,
} from 'siteLayout/readerPresentation';
import {
  DraftTag,
  Enforcement,
  Entry,
  entryFacesSameZone,
  EntrySide,
  isDuplicateName,
  Layout,
  OUTSIDE_LABEL,
  readerAssignmentName,
  sideFacingLabel,
} from 'siteLayout';
import {
  draftTagBadge,
  enforcementLabels,
} from 'containers/jobsiteConfiguration/tabs/siteLayout/labels';
import { NameField } from './NameField';

/** Option value for a side that faces the outside world rather than a zone. */
export const OUTSIDE_OPTION_VALUE = 'OUTSIDE';

export type SideReader = {
  id: string;
  name: string;
  portLabel: string;
  kind?: ReaderDisplayFields['kind'];
  health?: ReaderDisplayFields['health'];
  /** Named where it is placed, but not offered for placing. */
  missing?: boolean;
};

/** A reader's name, led on its line by the reader's kind once the site's readers say it. */
export function ReaderName({
  reader,
  className,
}: {
  reader: SideReader;
  className: string;
}): React.ReactElement {
  return (
    <span className="odin-flex odin-items-start odin-gap-2">
      {reader.kind && (
        <span className="odin-flex-shrink-0">
          <ReaderKindBadge kind={reader.kind} />
        </span>
      )}
      <span className={`${className} odin-min-w-0 odin-break-words`}>
        {reader.name}
      </span>
    </span>
  );
}

/** A reader's health, once the site's readers say it. */
export function ReaderDetails({
  reader,
}: {
  reader: SideReader;
}): React.ReactElement | null {
  if (!reader.kind || !reader.health) return null;
  return (
    <span className="odin-block odin-text-xs odin-text-gray-500">
      <ReaderHealth
        reader={{
          kind: reader.kind,
          health: reader.health,
          missing: Boolean(reader.missing),
        }}
      />
    </span>
  );
}

export type EntryEditorProps = {
  layout: Layout;
  entry: Entry;
  tag: DraftTag;
  readOnly: boolean;
  editingDraft: boolean;
  /** Off when the container already titles the entry and shows its tag, as a drawer does. */
  showHeading?: boolean;
  /** Each side's Avigilon entry; absent where the revision shown says nothing about them. */
  avigilonStatus?: React.ReactNode;
  /** The site's reader devices only, keyed by reader device id; never populated from layout history. */
  readersById: Map<string, SideReader>;
  onRename: (name: string) => Promise<boolean> | void;
  onSetFaces: (side: EntrySide, value: string) => void;
  onSetEnforcement: (side: EntrySide, enforcement: Enforcement) => void;
  /** Opens the drawer's assign step for the crossing into this side. */
  onAssignReader: (side: EntrySide) => void;
  onRemoveReader: (readerDeviceId: string) => void;
  /** What stopped the last Save, shown until the next attempt. */
  errors?: string[];
  /** Sides whose access needs a reader but has none, flagged after a Save attempt. */
  invalidSideIds?: string[];
  /** Save has been tried: every field shows its error, touched or not. */
  submitted?: boolean;
};

const classes = {
  // Sections breathe: the two crossings must not read as one block.
  panel: 'odin-flex odin-flex-col odin-gap-y-8',
  heading: 'odin-flex odin-items-start odin-justify-between odin-gap-3',
  kicker:
    'odin-text-xs odin-font-semibold odin-uppercase odin-tracking-wide odin-text-gray-500',
  title:
    'odin-text-2xl odin-font-semibold odin-tracking-tight odin-text-gray-900',
  // The same section heading the devices drawer uses.
  section: 'odin-space-y-4',
  sectionTitle: 'odin-text-base odin-font-medium odin-text-gray-900',
  fields: 'odin-grid odin-grid-cols-1 odin-gap-4 sm:odin-grid-cols-3',
  readersHeader:
    'odin-flex odin-items-center odin-justify-between odin-gap-2 odin-pt-2',
  // The same label the form fields above it use.
  readersLabel: 'odin-text-sm odin-font-medium odin-text-gray-900',
  // The table keeps a cell to one line; a reader's long name wraps instead.
  readerCard: 'odin-block odin-whitespace-normal odin-py-3',
  readerName: 'odin-font-medium odin-text-gray-900',
  readerPort: 'odin-text-xs odin-text-gray-500',
};

export const entryNameError = (layout: Layout, entry: Entry): string | null => {
  if (!entry.name.trim()) return 'An entry needs a name.';
  if (isDuplicateName(layout.entries, entry)) {
    return `An entry named ${entry.name.trim()} already exists in this layout.`;
  }
  return null;
};

const ENFORCEMENTS: Enforcement[] = [
  'ENFORCED',
  'IDENTIFY_ONLY',
  'NOT_TRACKED',
];
const enforcementOptions: SelectOptionElement<Enforcement>[] = ENFORCEMENTS.map(
  (value) => ({ value, label: enforcementLabels[value] }),
);

/** The zones a side may face: every zone but the other side's, and Outside unless the other side is. */
const facesOptions = (
  layout: Layout,
  other: EntrySide,
): SelectOptionElement[] => [
  ...layout.zones
    .filter((zone) => zone.id !== other.zoneId)
    .map((zone) => ({ value: zone.id, label: zone.name })),
  ...(other.kind === 'OUTSIDE'
    ? []
    : [{ value: OUTSIDE_OPTION_VALUE, label: OUTSIDE_LABEL }]),
];

const facesValue = (
  layout: Layout,
  side: EntrySide,
  options: SelectOptionElement[],
): SelectOptionElement | null =>
  side.kind === 'OUTSIDE'
    ? { value: OUTSIDE_OPTION_VALUE, label: OUTSIDE_LABEL }
    : options.find((option) => option.value === side.zoneId) ?? {
        value: side.zoneId ?? '',
        label: sideFacingLabel(layout, side),
      };

type ReaderRow = SideReader & {
  menuItems: MenuItemProps[];
};

function ReaderCell({
  row,
}: {
  row: { original: ReaderRow };
}): React.ReactElement {
  return (
    <span className={classes.readerCard}>
      <ReaderName reader={row.original} className={classes.readerName} />
      <span className={`${classes.readerPort} odin-block`}>
        {row.original.portLabel}
      </span>
      <ReaderDetails reader={row.original} />
    </span>
  );
}

const readerColumns: Column<ReaderRow>[] = [
  { id: 'reader', Header: 'Reader', accessor: 'name', Cell: ReaderCell },
  {
    id: 'actions',
    Header: '',
    Cell: DropdownButtonCell,
    componentProps: (row: ReaderRow): DropdownButtonCellComponentProps => ({
      cellAlignment: 'right',
      menuItems: row.menuItems,
    }),
  },
];

/** The readers placed on one crossing; assigning one is the drawer's own step. */
function CrossingReaders({
  readers,
  editing,
  onAssign,
  onRemove,
  invalid,
}: {
  readers: SideReader[];
  editing: boolean;
  invalid: boolean;
  onAssign: () => void;
  onRemove: (readerDeviceId: string) => void;
}): React.ReactElement {
  const rows = React.useMemo<ReaderRow[]>(
    () =>
      readers.map((reader) => ({
        ...reader,
        menuItems: editing
          ? [
              {
                icon: TrashIcon,
                text: 'Remove',
                theme: 'danger',
                onClick: (): void => onRemove(reader.id),
              },
            ]
          : [],
      })),
    [readers, editing, onRemove],
  );
  return (
    <>
      <div className={classes.readersHeader}>
        <span className={classes.readersLabel}>Readers</span>
        {editing && (
          <Button
            icon={PlusIcon}
            text="Assign reader"
            size="sm"
            theme="white"
            onClick={onAssign}
          />
        )}
      </div>
      <Table
        headerRowClassName="odin-hidden md:odin-hidden print:odin-hidden"
        columns={readerColumns}
        data={rows}
        noResultsText="No reader assigned"
        remote
        disableGlobalFilter
        disableSortBy
      />
      {invalid && (
        <Alert
          type="danger"
          text="Assign at least one reader, or set Access to None."
        />
      )}
    </>
  );
}

/**
 * The selected entry: its name, the crossing into it and the crossing out of
 * it, each with its access rule and readers. Removal is a row action on the
 * entries list. From and To label sides A and B: the From → To crossing
 * admits into side B; the To → From crossing is the reverse.
 */
export function EntryEditor({
  layout,
  entry,
  tag,
  readOnly,
  editingDraft,
  showHeading = true,
  avigilonStatus,
  readersById,
  onRename,
  onSetFaces,
  onSetEnforcement,
  onAssignReader,
  onRemoveReader,
  errors = [],
  invalidSideIds = [],
  submitted = false,
}: EntryEditorProps): React.ReactElement {
  const badge = draftTagBadge(tag);
  const { sideA, sideB } = entry;
  const readersInto = (side: EntrySide): SideReader[] =>
    layout.readerAssignments
      .filter((assignment) => assignment.admitsIntoSideId === side.id)
      .map((assignment) => {
        const siteReader = readersById.get(assignment.readerDeviceId);
        return {
          id: assignment.readerDeviceId,
          name: readerAssignmentName(assignment),
          // The layout API names the reader; the site's readers say where it
          // is wired and whether the provider still lists it.
          portLabel:
            siteReader?.portLabel ?? 'Controller and port details unavailable',
          kind: siteReader?.kind,
          health: siteReader?.health,
          missing: siteReader?.missing,
        };
      });
  const fromOptions = facesOptions(layout, sideB);
  const toOptions = facesOptions(layout, sideA);
  const fromName = sideFacingLabel(layout, sideA);
  const toName = sideFacingLabel(layout, sideB);
  const accessValue = (side: EntrySide): SelectOptionElement<Enforcement> =>
    enforcementOptions.find((option) => option.value === side.enforcement) ??
    enforcementOptions[0];
  const editing = editingDraft && !readOnly;

  const crossingReaders = (side: EntrySide): React.ReactNode =>
    side.enforcement === 'NOT_TRACKED' ? null : (
      <CrossingReaders
        readers={readersInto(side)}
        editing={editing}
        onAssign={() => onAssignReader(side)}
        onRemove={onRemoveReader}
        invalid={invalidSideIds.includes(side.id)}
      />
    );

  return (
    <div className={classes.panel}>
      {showHeading && (
        <div className={classes.heading}>
          <div>
            <p className={classes.kicker}>Entry</p>
            <h2 className={classes.title}>{entry.name || 'Unnamed entry'}</h2>
          </div>
          {badge && (
            <Badge text={badge.text} color={badge.color as BadgeColor} />
          )}
        </div>
      )}

      {errors.length > 0 && <Alert type="danger" text={errors.join(' ')} />}

      <NameField
        value={entry.name}
        placeholder="The identifier an installer would use: North Gate, Door 14"
        error={entryNameError(layout, entry)}
        submitted={submitted}
        disabled={readOnly}
        onCommit={onRename}
      />

      <section className={classes.section} aria-label="From → To">
        <h3 className={classes.sectionTitle}>From → To</h3>
        <div className={classes.fields}>
          <Select
            name="from"
            label="From"
            value={facesValue(layout, sideA, fromOptions)}
            options={fromOptions}
            isDisabled={readOnly}
            isSearchable={false}
            isClearable={false}
            onChange={(option: SelectOptionElement | null) => {
              if (option) onSetFaces(sideA, option.value);
            }}
          />
          <Select
            name="to"
            label="To"
            value={facesValue(layout, sideB, toOptions)}
            options={toOptions}
            isDisabled={readOnly}
            isSearchable={false}
            isClearable={false}
            onChange={(option: SelectOptionElement | null) => {
              if (option) onSetFaces(sideB, option.value);
            }}
          />
          <Select
            name="entry-access"
            label="Access"
            value={accessValue(sideB)}
            options={enforcementOptions}
            isDisabled={readOnly}
            isSearchable={false}
            isClearable={false}
            onChange={(option: SelectOptionElement<Enforcement> | null) => {
              if (option) onSetEnforcement(sideB, option.value);
            }}
          />
        </div>
        {crossingReaders(sideB)}
      </section>

      <section className={classes.section} aria-label="To → From">
        <h3 className={classes.sectionTitle}>To → From</h3>
        <div className={classes.fields}>
          <FieldContainer name="exit-from" label="From">
            <Input name="exit-from" value={toName} disabled />
          </FieldContainer>
          <FieldContainer name="exit-to" label="To">
            <Input name="exit-to" value={fromName} disabled />
          </FieldContainer>
          <Select
            name="exit-access"
            label="Access"
            value={accessValue(sideA)}
            options={enforcementOptions}
            isDisabled={readOnly}
            isSearchable={false}
            isClearable={false}
            onChange={(option: SelectOptionElement<Enforcement> | null) => {
              if (option) onSetEnforcement(sideA, option.value);
            }}
          />
        </div>
        {crossingReaders(sideA)}
      </section>

      {/* From and To offer no choice that faces the same zone, so only a stored entry can; Save says so. */}
      {submitted && entryFacesSameZone(entry) && (
        <Alert
          type="danger"
          text="From and To are the same zone. Change one so the entry joins two different zones, or a zone and Outside."
        />
      )}

      {avigilonStatus}
    </div>
  );
}
